import React, { useState } from 'react';
import { formatPrice } from '../../utils/formatPrice';
import { useLanguage } from '../../context/LanguageContext';
import { formatGeorgianPhone, getPhoneDigits, isValidGeorgianMobile } from '../../utils/georgianPhone';
import { cartRequiresKnifeVerification } from '../../utils/knifeVerification';
import { getDisplayPrice } from '../../utils/productPricing';
import './CartSummary.css';

const DELIVERY_OPTIONS = [
  { id: 'tbilisi', label: 'თბილისი', fee: 6 },
  { id: 'region', label: 'რეგიონი', fee: 9 },
];

const PAYMENT_OPTIONS = [
  { id: 'courier', labelKey: 'checkout.payWithCourier' },
  { id: 'installment', labelKey: 'product.installment' },
  { id: 'card', labelKey: 'checkout.payByCard' },
];

const INITIAL_CUSTOMER_FORM = {
  phone: '',
  customerName: '',
  deliveryCity: '',
  deliveryRegionCity: '',
  address: '',
  personalId: '',
  knifeGuidelinesAccepted: false,
};

const hasFirstAndLastName = (value) => String(value || '').trim().split(/\s+/).filter(Boolean).length >= 2;

const CartSummary = ({ cart, onCheckout }) => {
  const { t } = useLanguage();
  const [paymentMethod, setPaymentMethod] = useState('courier');
  const [customerForm, setCustomerForm] = useState(INITIAL_CUSTOMER_FORM);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const total = cart?.items?.reduce(
    (sum, item) => sum + getDisplayPrice(item.product || item) * (item.quantity || item.qty || 1),
    0
  ) || 0;
  const selectedDeliveryOption = DELIVERY_OPTIONS.find((option) => option.id === customerForm.deliveryCity);
  const isRegionDelivery = selectedDeliveryOption?.id === 'region';
  const deliveryFee = selectedDeliveryOption?.fee || 0;
  const checkoutTotal = total + deliveryFee;
  const requiresKnifeVerification = cartRequiresKnifeVerification(cart);

  const handleCustomerChange = (event) => {
    const { name, type, checked, value } = event.target;
    setError('');
    setCustomerForm((current) => {
      const nextForm = {
        ...current,
        [name]: type === 'checkbox'
          ? checked
          : (name === 'phone' ? formatGeorgianPhone(value) : value),
      };

      if (name === 'deliveryCity' && !value) {
        nextForm.address = '';
      }

      if (name === 'deliveryCity' && value !== 'region') {
        nextForm.deliveryRegionCity = '';
      }

      return nextForm;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const customerName = customerForm.customerName.trim();
    const deliveryCity = customerForm.deliveryCity.trim();
    const deliveryOption = DELIVERY_OPTIONS.find((option) => option.id === deliveryCity);
    const deliveryRegionCity = customerForm.deliveryRegionCity.trim();
    const address = customerForm.address.trim();
    const phone = getPhoneDigits(customerForm.phone);

    if (!customerName || !deliveryOption || !address || !phone || !hasFirstAndLastName(customerName) || (deliveryOption.id === 'region' && !deliveryRegionCity)) {
      setError(t('checkout.requiredFields'));
      return;
    }

    if (!isValidGeorgianMobile(phone)) {
      setError(t('checkout.invalidPhone'));
      return;
    }

    if (!termsAccepted) {
      setError(t('checkout.termsRequired'));
      return;
    }

    if (requiresKnifeVerification && (!customerForm.personalId.trim() || !customerForm.knifeGuidelinesAccepted)) {
      setError(t('checkout.knifeVerificationRequired'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const deliveryLabel = deliveryOption.id === 'region'
        ? `${deliveryOption.label} - ${deliveryRegionCity}`
        : deliveryOption.label;

      await onCheckout({
        customer: {
          name: customerName,
          phone,
          address,
          deliveryCity: deliveryLabel,
          deliveryArea: deliveryOption.label,
          deliveryRegionCity: deliveryOption.id === 'region' ? deliveryRegionCity : '',
        },
        paymentMethod,
        shippingFee: deliveryOption.fee,
        acceptedTerms: termsAccepted,
        knifeVerification: requiresKnifeVerification
          ? {
              personalId: customerForm.personalId.trim(),
              ageConfirmed: true,
              guidelinesAccepted: customerForm.knifeGuidelinesAccepted,
            }
          : null,
        card: null,
      });
      setCustomerForm(INITIAL_CUSTOMER_FORM);
      setTermsAccepted(false);
    } catch (checkoutError) {
      setError(checkoutError.message || t('checkout.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="cart-checkout" onSubmit={handleSubmit}>
      <h2>{t('cart.orderSummary')}</h2>

      <div className="cart-checkout__totals">
        <div className="cart-checkout__row">
          <span>{t('common.subtotal')}:</span>
          <span>{formatPrice(total)}</span>
        </div>
        <div className="cart-checkout__row">
          <span>{t('common.shipping')}:</span>
          <span>{selectedDeliveryOption ? formatPrice(deliveryFee) : '-'}</span>
        </div>
        <div className="cart-checkout__row cart-checkout__row--total">
          <span>{t('common.total')}:</span>
          <span>{formatPrice(checkoutTotal)}</span>
        </div>
      </div>

      <div className="cart-checkout__payment-options" role="radiogroup" aria-label={t('checkout.paymentOptions')}>
        {PAYMENT_OPTIONS.map((option) => (
          <label
            key={option.id}
            className={`cart-checkout__payment-option ${paymentMethod === option.id ? 'is-selected' : ''}`}
          >
            <input
              type="radio"
              name="paymentMethod"
              value={option.id}
              checked={paymentMethod === option.id}
              onChange={(event) => {
                setPaymentMethod(event.target.value);
                setError('');
              }}
            />
            <span className="cart-checkout__payment-dot" aria-hidden="true" />
            <span>{t(option.labelKey)}</span>
          </label>
        ))}
      </div>

      {paymentMethod !== 'card' && (
        <p className="cart-checkout__contact-note">{t('checkout.contactNotice')}</p>
      )}

      {paymentMethod === 'card' && (
        <p className="cart-checkout__contact-note">{t('checkout.cardRedirectNote')}</p>
      )}

      <label className="cart-checkout__field">
        <span>{t('checkout.phoneNumber')}</span>
        <input
          type="tel"
          name="phone"
          value={customerForm.phone}
          onChange={handleCustomerChange}
          placeholder={t('checkout.phoneNumber')}
          autoComplete="tel"
          inputMode="numeric"
          maxLength={12}
        />
      </label>

      <label className="cart-checkout__field">
        <span>{t('checkout.customerName')}</span>
        <input
          type="text"
          name="customerName"
          value={customerForm.customerName}
          onChange={handleCustomerChange}
          placeholder={t('checkout.customerName')}
          autoComplete="name"
        />
      </label>

      <label className="cart-checkout__field">
        <span>{t('checkout.deliveryRegion')}</span>
        <select
          name="deliveryCity"
          value={customerForm.deliveryCity}
          onChange={handleCustomerChange}
          required
        >
          <option value="">{t('checkout.deliveryRegionPlaceholder')}</option>
          {DELIVERY_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {isRegionDelivery && (
        <label className="cart-checkout__field">
          <span>{t('checkout.deliveryCity')}</span>
          <input
            type="text"
            name="deliveryRegionCity"
            value={customerForm.deliveryRegionCity}
            onChange={handleCustomerChange}
            placeholder={t('checkout.deliveryCityPlaceholder')}
            autoComplete="address-level2"
            required
          />
        </label>
      )}

      {selectedDeliveryOption && (
        <label className="cart-checkout__field">
          <span>{t('checkout.address')}</span>
          <input
            type="text"
            name="address"
            value={customerForm.address}
            onChange={handleCustomerChange}
            placeholder={t('checkout.addressPlaceholder')}
            autoComplete="street-address"
            required
          />
        </label>
      )}

      {requiresKnifeVerification && (
        <div className="cart-checkout__knife-check">
          <strong>{t('checkout.knifeVerificationTitle')}</strong>
          <label className="cart-checkout__field">
            <span>{t('checkout.personalId')}</span>
            <input
              type="text"
              name="personalId"
              value={customerForm.personalId}
              onChange={handleCustomerChange}
              placeholder={t('checkout.personalIdPlaceholder')}
              autoComplete="off"
              required
            />
          </label>
          <label className={`cart-checkout__terms ${customerForm.knifeGuidelinesAccepted ? 'is-checked' : ''}`}>
            <input
              type="checkbox"
              name="knifeGuidelinesAccepted"
              checked={customerForm.knifeGuidelinesAccepted}
              onChange={handleCustomerChange}
            />
            <span className="cart-checkout__terms-box" aria-hidden="true" />
            <span>{t('checkout.knifeGuidelinesAgreement')}</span>
          </label>
        </div>
      )}

      <label className={`cart-checkout__terms ${termsAccepted ? 'is-checked' : ''}`}>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(event) => {
            setTermsAccepted(event.target.checked);
            setError('');
          }}
        />
        <span className="cart-checkout__terms-box" aria-hidden="true" />
        <span>
          {t('checkout.termsAgree')}{' '}
          <a href="/terms-and-conditions">{t('checkout.termsLink')}</a>
        </span>
      </label>

      {error && <p className="cart-checkout__error">{error}</p>}

      <button type="submit" className="cart-checkout__submit" disabled={submitting}>
        {submitting ? t('checkout.ordering') : t('checkout.order')}
      </button>
    </form>
  );
};

export default CartSummary;
